import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, Check, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Pricing = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [checkingSubscription, setCheckingSubscription] = useState(true);

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`, { withCredentials: true });
      setSubscription(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setCheckingSubscription(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription?')) {
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/subscription/cancel`, {}, { withCredentials: true });
      toast.success('Subscription cancelled successfully');
      await fetchSubscription();
    } catch (error) {
      console.error('Cancel error:', error);
      toast.error('Failed to cancel subscription');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    // Prevent double clicks
    if (loading) return;
    
    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(
        `${API}/subscription/create-checkout`,
        { origin_url: originUrl },
        { withCredentials: true }
      );
      
      // Check if it's a free trial activation
      if (response.data.free_trial) {
        toast.success('First month free trial activated!');
        navigate('/dashboard');
      } else if (response.data.url) {
        // Redirect to Stripe checkout for paid subscription
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      if (error.response?.status === 401) {
        toast.error('Please sign in first');
        navigate('/');
      } else {
        toast.error('Failed to create checkout session');
        setLoading(false); // Only reset on error
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f0f7ff] via-white to-[#f8f9fb]">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            data-testid="back-button"
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#2563eb] to-[#1e40af] flex items-center justify-center">
              <Heart className="w-6 h-6 text-white" fill="currentColor" />
            </div>
            <span className="text-xl font-bold text-gray-900">Healthcare Hearts</span>
          </div>
        </div>
      </header>

      {/* Pricing Section */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Simple, Transparent Pricing
          </h1>
          <p className="text-lg text-gray-600">
            One plan. Full access. No hidden fees.
          </p>
        </div>

        {/* Pricing Card */}
        <div className="max-w-lg mx-auto">
          <div className="bg-white rounded-3xl shadow-xl border-2 border-[#2563eb] overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-br from-[#2563eb] to-[#1e40af] p-8 text-center text-white">
              <div className="inline-block bg-white/20 text-white text-sm font-semibold px-4 py-1 rounded-full mb-3">
                FIRST MONTH FREE
              </div>
              <h2 className="text-2xl font-bold mb-2">Premium Membership</h2>
              <div className="flex items-baseline justify-center gap-2">
                <span className="text-5xl font-bold">$20</span>
                <span className="text-xl opacity-90">/month</span>
              </div>
              <p className="text-sm opacity-90 mt-2">After first month free trial</p>
            </div>

            {/* Features */}
            <div className="p-8">
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-green-600" />
                  </div>
                  <span className="text-gray-700">Unlimited profile browsing</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-green-600" />
                  </div>
                  <span className="text-gray-700">Unlimited likes and matches</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-green-600" />
                  </div>
                  <span className="text-gray-700">In-app messaging with matches</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-green-600" />
                  </div>
                  <span className="text-gray-700">AI-powered match suggestions</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-green-600" />
                  </div>
                  <span className="text-gray-700">Advanced filters by specialty and location</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-green-600" />
                  </div>
                  <span className="text-gray-700">Priority support</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-green-600" />
                  </div>
                  <span className="text-gray-700">Cancel anytime</span>
                </li>
              </ul>

              {subscription?.has_subscription ? (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                    <p className="text-green-800 font-semibold">Active Subscription</p>
                    {subscription.subscription?.current_period_end && (
                      <p className="text-sm text-green-600 mt-1">
                        Renews: {new Date(subscription.subscription.current_period_end).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <Button
                    onClick={handleCancelSubscription}
                    disabled={loading}
                    data-testid="cancel-subscription-button"
                    variant="outline"
                    className="w-full border-red-300 text-red-600 hover:bg-red-50 py-4 rounded-xl text-lg font-semibold disabled:opacity-50"
                  >
                    {loading ? 'Processing...' : 'Cancel Subscription'}
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={handleSubscribe}
                  disabled={loading || checkingSubscription}
                  data-testid="subscribe-button"
                  className="w-full bg-[#2563eb] hover:bg-[#1e40af] text-white py-4 rounded-xl text-lg font-semibold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Processing...' : checkingSubscription ? 'Loading...' : 'Subscribe Now'}
                </Button>
              )}

              <p className="text-center text-sm text-gray-500 mt-4">
                Secure payment powered by Stripe
              </p>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-center text-gray-900 mb-8">Frequently Asked Questions</h2>
          <div className="space-y-4">
            <div className="bg-white p-6 rounded-xl border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-2">Can I cancel anytime?</h3>
              <p className="text-gray-600">Yes! You can cancel your subscription at any time from your account settings. No questions asked.</p>
            </div>
            <div className="bg-white p-6 rounded-xl border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-2">Is my payment information secure?</h3>
              <p className="text-gray-600">Absolutely. All payments are processed securely through Stripe. We never store your credit card information.</p>
            </div>
            <div className="bg-white p-6 rounded-xl border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-2">What happens after I subscribe?</h3>
              <p className="text-gray-600">You'll get immediate access to all premium features including unlimited browsing, likes, matches, and messaging.</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Pricing;
